<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- 登録・編集・参照ダイアログ -->
        <edit-dialog
          v-model="editDialog"
          :loading="loading"
          :readonly="readOnly"
          :validationRules="validationRules"
          :locales="locales"
          :primaryLocaleCode="primaryLocaleCode"
          :infoGuideTypes="infoGuideTypes"
          :infoGuideTypeCode.sync="editedInfoGuideTypeCode"
          :municipInfoGuideTypeCodes="municipInfoGuideTypeCodes"
          :noteInfoGuideTypeCodes="noteInfoGuideTypeCodes"
          :municipalities="municipalities"
          :municipCode.sync="editedMunicipCode"
          :isNew="isNewEditedItems"
          :items="editedItems"
          @save="handleSaveEditDialog"
          @close="handleCloseEditDialog"
        ></edit-dialog>
      </v-toolbar>
      <v-container fluid>
        <!-- メッセージ -->
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          情報ガイドマスタは下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
        </p>
        <v-row class="ml-1">
          <v-col>
            <!-- 情報タイプ -->
            <v-radio-group row v-model="infoGuideTypeCode">
              <v-radio
                v-for="type in infoGuideTypes"
                :key="type.code"
                :label="type.name"
                :value="type.code"
              ></v-radio>
            </v-radio-group>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
        <v-row class="ml-1">
          <v-col cols="12">
            <p>
              部分一致で検索することができます。<br />
              <template v-if="showsNote">
                検索はタイトル、説明文の項目が対象です。<br />
              </template>
            </p>
          </v-col>
          <v-col cols="12">
            <!-- 検索条件: フリーテキスト、または、タイトル -->
            <v-text-field
              v-model="freeText"
              :label="freeTextLabel"
              :placeholder="freeTextPlaceholder"
              append-icon="mdi-magnify"
              hide-details
              clearable
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <!-- 検索条件: 市町村 -->
            <v-select
              v-model="municipCode"
              v-if="showsMunicipalities"
              :items="municipalities"
              :placeholder="config.PLACEHOLDER_SELECT"
              label="市町村"
              item-text="municip_name"
              item-value="municip_code"
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="ml-4 mt-n4" style="font-size: small" no-gutters>
          <!-- 凡例 -->
          <v-col cols="12">【凡例】</v-col>
          <v-col cols="12" class="d-flex">
            <div class="pr-4">各言語</div>
            <div>
              ○&#xff1a; 登録あり（クリックでプレビュー表示）<br />
              ー&#xff1a; 登録なし
            </div>
            <div class="pl-8 pr-2">表示順&#xff1a;</div>
            <div v-if="readOnly">
              住民向けスマホアプリ画面で表示する順番になります。
            </div>
            <div v-else>
              住民向けスマホアプリ画面で表示する順番になります。<br />
              表示順を変更したい場合は、数字を変更して、「表示順更新」ボタンを押下してください。
            </div>
            <!-- ツールチップ -->
            <v-tooltip right :open-on-hover="false" color="grey lighten-4">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!readOnly"
                  icon
                  @click="on.click"
                  @blur="on.blur"
                  retain-focus-on-click
                >
                  <v-icon>mdi-help-circle-outline</v-icon>
                </v-btn>
              </template>
              <v-container fluid style="color: black">
                <v-row no-gutters>
                  <v-col cols="12" style="padding-left: 2em; text-indent: -2em">
                    例）タイトル３をタイトル１の次に変更したい場合<br />
                    タイトル３の表示順を11に変更し「表示順更新」ボタンを押下してください。<br />
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-4">
                  <v-spacer></v-spacer>
                  <v-col cols="5">
                    (更新前)<br />
                    タイトル１ 表示順:10<br />
                    タイトル２ 表示順:20<br />
                    タイトル３ 表示順:30<br />
                  </v-col>
                  <v-col cols="5">
                    (更新後)<br />
                    タイトル１ 表示順:10<br />
                    タイトル３ 表示順:20<br />
                    タイトル２ 表示順:30<br />
                  </v-col>
                  <v-spacer></v-spacer>
                </v-row>
              </v-container>
            </v-tooltip>
            <v-spacer></v-spacer>
            <!-- 表示順更新ボタン -->
            <div class="align-self-end">
              <v-btn
                v-if="!readOnly"
                small
                class="mx-2"
                color="primary"
                :disabled="!enableUpdateDispNo"
                @click="handleUpdateDispNo"
                >表示順更新</v-btn
              >
            </div>
          </v-col>
        </v-row>
        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- 一覧表 -->
          <top-operation-data-table
            ref="table"
            :headers="tableHeaders"
            :items="filteredGuideMstRecords"
            @pagination="$nextTick(() => $refs.form.validate())"
            locale="ja-jp"
            must-sort
          >
            <template v-slot:[`item.editable_disp_no`]="{ item }">
              <!-- 表示順 -->
              <div v-if="readOnly" style="text-align: right">
                {{ item.editable_disp_no }}
              </div>
              <div v-else>
                <v-text-field
                  v-model="item.editable_disp_no"
                  class="align-right-input"
                  type="number"
                  :rules="dispNoRules"
                  hide-details
                  single-line
                  outlined
                  dense
                  @blur="$refs.form.validate()"
                ></v-text-field>
              </div>
            </template>
            <template v-slot:[`item.ja.link_url`]="{ item }">
              <!-- リンク先ＵＲＬ: 日本語 -->
              <a
                v-if="item.ja.link_url"
                :href="item.ja.link_url"
                target="_blank"
                rel="noreferrer noopener"
                >○</a
              >
              <template v-else>ー</template>
            </template>
            <template v-slot:[`item.en.link_url`]="{ item }">
              <!-- リンク先ＵＲＬ: 英語 -->
              <a
                v-if="item.en && item.en.link_url"
                :href="item.en.link_url"
                target="_blank"
                rel="noreferrer noopener"
                >○</a
              >
              <template v-else>ー</template>
            </template>
            <template v-slot:[`item.ko.link_url`]="{ item }">
              <!-- リンク先ＵＲＬ: 韓国語 -->
              <a
                v-if="item.ko && item.ko.link_url"
                :href="item.ko.link_url"
                target="_blank"
                rel="noreferrer noopener"
                >○</a
              >
              <template v-else>ー</template>
            </template>
            <template v-slot:[`item.zhcn.link_url`]="{ item }">
              <!-- リンク先ＵＲＬ: 中国語（簡体） -->
              <a
                v-if="item.zhcn && item.zhcn.link_url"
                :href="item.zhcn.link_url"
                target="_blank"
                rel="noreferrer noopener"
                >○</a
              >
              <template v-else>ー</template>
            </template>
            <template v-slot:[`item.zhtw.link_url`]="{ item }">
              <!-- リンク先ＵＲＬ: 中国語（繁体） -->
              <a
                v-if="item.zhtw && item.zhtw.link_url"
                :href="item.zhtw.link_url"
                target="_blank"
                rel="noreferrer noopener"
                >○</a
              >
              <template v-else>ー</template>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <!-- 操作 -->
              <template v-if="readOnly">
                <!-- 参照ボタン -->
                <v-btn
                  small
                  class="mx-2"
                  color="primary"
                  @click="handleViewOrEdit(item)"
                  >参照</v-btn
                >
              </template>
              <template v-else>
                <!-- 編集ボタン -->
                <v-btn
                  small
                  class="mx-2"
                  color="primary"
                  @click="handleViewOrEdit(item)"
                  >編集</v-btn
                >
                <!-- 削除ボタン -->
                <v-btn
                  small
                  class="mx-2"
                  color="primary"
                  @click="handleDelete(item)"
                  >削除</v-btn
                >
              </template>
            </template>
          </top-operation-data-table>
        </v-form>
      </v-container>
    </v-sheet>
    <!-- ダイアログ -->
    <error-dialog ref="errorDialog" />
    <confirm-dialog ref="confirmDialog" />
    <!-- オーバーレイ -->
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ConfirmDialog from "@/components/ConfirmDialog";
import config from "@/js/const";
import ErrorDialog from "@/components/ErrorDialog";
import messageUtils from "@/mixins/message-utils";
import TopOperationDataTable from "@/components/ui/TopOperationDataTable";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";
import EditDialog from "@/components/info-guide-mst/EditDialog";

const allAreaRepository = RepositoryFactory.get("allArea");
const infoGuideMstRepository = RepositoryFactory.get("infoGuideMst");

// エラーメッセージ
const ERROR_MSG_REQUIRED = "必須項目です。";
const ERROR_MSG_NUMBERS_ONLY = "数値で入力してください";
const ERROR_MSG_DUPLICATE =
  "表示順の数字が重複しています。重複しないよう入力してください。";

/**
 * バリデーションルールを作成する。
 * @function
 * @returns {Object} バリデーションルール
 */
const getRules = () => ({
  required: () => v => !!v || ERROR_MSG_REQUIRED,
  numbersOnly: () => v => /^\d+$/.test(v) || ERROR_MSG_NUMBERS_ONLY,
  duplicated: duplicatedArray => v =>
    !duplicatedArray.includes(v) || ERROR_MSG_DUPLICATE
});

/**
 * 表示順を10倍した、一覧表編集用表示順項目を追加する。
 * @function
 * @param {Object} record 表示順を含むレコード
 * @returns {Object} 表示順を10倍した編集用表示順を含むレコード
 */
const addEditableDispNo = record => ({
  ...record,
  editable_disp_no: (Number(record.disp_no) * 10).toString()
});

/**
 * 編集用表示順の昇順でソートする。
 */
const orderByEditableDispNo = (a, b) =>
  Number(a.editable_disp_no) - Number(b.editable_disp_no);

/**
 * 市町村リストを取得する。
 * @function
 * @return {Object[]} 市町村リスト
 */
const fetchMunicipalities = async () => {
  return (
    await allAreaRepository.getAllArea()
  ).data.results[0].municipalities.filter(({ disp_flg }) => disp_flg !== "1");
};

/**
 * 情報ガイドマスタレコードリストを取得する。
 * @function
 * @param {String} 画面ID
 * @return {Object[]} 情報ガイドマスタレコード
 */
const fetchInfoGuideMstRecords = async screenId => {
  return (await infoGuideMstRepository.list(screenId)).data.results
    .map(addEditableDispNo)
    .sort(orderByEditableDispNo);
};

/**
 * 一覧表のヘッダーを作成する。
 * @function
 * @param {boolean} showsNote 説明文を表示するかどうかを示す値
 * @returns {Object[]} ヘッダー
 */
const createTableHeaders = showsNote => [
  { text: "タイトル", value: "ja.title" },
  {
    text: "説明文",
    value: "ja.note",
    align: showsNote ? "" : " d-none"
  },
  {
    text: "表示順",
    value: "editable_disp_no",
    align: "center",
    width: "10em",
    sortable: false
  },
  {
    text: "日本語",
    value: "ja.link_url",
    align: "center",
    width: "6em",
    sortable: false
  },
  {
    text: "英語",
    value: "en.link_url",
    align: "center",
    width: "6em",
    sortable: false
  },
  {
    text: "韓国語",
    value: "ko.link_url",
    align: "center",
    width: "6em",
    sortable: false
  },
  {
    text: "中国語\n（簡体）",
    value: "zhcn.link_url",
    align: "center",
    class: "text-pre",
    width: "6em",
    sortable: false
  },
  {
    text: "中国語\n（繁体）",
    value: "zhtw.link_url",
    align: "center",
    class: "text-pre",
    width: "6em",
    sortable: false
  },
  { text: "操作", value: "actions", width: "14.5em", sortable: false }
];

/**
 * 情報タイプリストを取得する。
 * @function
 * @return {Object[]} 情報タイプリスト
 */
const getInfoGuideTypes = () => [
  { code: "00", name: "防災リンク", needsNote: true },
  { code: "03", name: "防災のしおり" },
  { code: "01", name: "避難地図", needsMunicip: true },
  { code: "02", name: "地域防災計画", needsMunicip: true }
];

/**
 * 言語リストを取得する。
 * @function
 * @return {Object[]} 言語リスト
 */
const getLocales = () => [
  { code: "ja", name: "日本語", isPrimary: true },
  { code: "en", name: "英語" },
  { code: "ko", name: "韓国語" },
  { code: "zhcn", name: "中国語\n（簡体）" },
  { code: "zhtw", name: "中国語\n（繁体）" }
];

/**
 * 新規登録ダイアログ用のアイテム配列を作成する。
 * @function
 * @returns {Object[]}
 */
const createEditedItems = () =>
  // 全言語のアイテムを作成
  getLocales().map(({ code, name }) => ({
    locale: code,
    localeName: name,
    title: "",
    note: "",
    url: "",
    // URLプレビューしたかどうかを示す値
    urlPreviewed: false
  }));

/**
 * APIレコードをダイアログアイテム形式に変換する。
 * @function
 * @param {Object} record APIレコード
 * @returns {Object[]} ダイアログアイテム
 */
const toEditedItems = record => {
  let items = createEditedItems();

  // 言語別の値を設定
  for (const item of items) {
    if (item.locale in record) {
      item.title = record[item.locale].title;
      item.note = record[item.locale].note;
      item.url = record[item.locale].link_url;
      // 既にURLが入力されている場合はプレビュー済みにする
      item.urlPreviewed = !!item.url;
    }
  }

  return items;
};

/**
 * ダイアログアイテムをAPIレコード形式に変換する。
 * なお、変換したAPIレコードには言語データしか含まれない。
 *
 * @function
 * @param {Object[]} ダイアログアイテム
 * @returns APIレコード
 */
const fromEditedItems = (items, needsNote = true) => {
  return Object.fromEntries(
    items.map(({ locale, title, note, url }) => [
      locale,
      { title, note: needsNote ? note : "", link_url: url }
    ])
  );
};

/**
 * 登録APIのペイロードを作成する。
 * @function
 * @param {Object} entry 言語を除く登録データ
 * @param {String} entry.info_gude_type 情報タイプ
 * @param {String} [entry.municip_code=0] 市町村コード
 * @param {String} edited 編集後言語レコード
 * @returns {Object} ペイロード
 */
const createCreatePayload = (
  { info_guide_type, municip_code = "0" },
  edited
) => {
  return {
    info_guide_type,
    municip_code,
    ...edited
  };
};

/**
 * 表示順更新用の更新APIのペイロードを作成する。
 * @function
 * @param {Object[]} records 編集用表示順が更新されたレコード
 * @returns {Object[]} ペイロード
 */
const createUpdateDispNoPayload = records => {
  // 編集用表示順の昇順で、表示順を振る。
  return records.sort(orderByEditableDispNo).map((record, index) =>
    Object.assign(
      {
        link_id: record.link_id,
        municip_code: record.municip_code,
        info_guide_type: record.info_guide_type,
        disp_no: index + 1
      },
      // 言語設定があればhashのみ追加
      ...getLocales()
        .map(({ code }) => code)
        .filter(locale => locale in record)
        .map(locale => ({
          [locale]: {
            hash: record[locale].hash
          }
        }))
    )
  );
};

/**
 * 更新APIのペイロードを作成する。
 * @function
 * @param {Object} original 更新前のレコード
 * @param {Object} edited 編集後言語レコード
 * @returns {Object[]} ペイロード
 */
const createUpdatePayload = (original, edited) => {
  return [
    Object.assign(
      {
        link_id: original.link_id,
        municip_code: original.municip_code,
        info_guide_type: original.info_guide_type,
        disp_no: original.disp_no
      },
      // 言語設定が両方ある場合、編集後で上書き。その他の場合、編集後を使用。
      // つまり、前者はhashがあり、後者はhashのないオブジェクトを設定する。
      ...getLocales().map(({ code: locale }) => ({
        [locale]:
          locale in original
            ? { ...original[locale], ...edited[locale] }
            : { ...edited[locale] }
      }))
    )
  ];
};

/**
 * 削除APIのペイロードを作成する。
 * @function
 * @param {Object} record 削除対象のレコード
 * @returns {Object} ペイロード
 */
const createDeletePayload = record => {
  return Object.assign(
    {
      link_id: record.link_id
    },
    // 言語設定があればhashのみ追加
    ...getLocales()
      .map(({ code }) => code)
      .filter(locale => locale in record)
      .map(locale => ({
        [locale]: {
          hash: record[locale].hash
        }
      }))
  );
};

export default {
  mixins: [messageUtils],

  components: {
    ConfirmDialog,
    ErrorDialog,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    EditDialog,
    TopOperationDataTable
  },

  data: () => ({
    overlay: false,
    loading: false,
    screenTitle: "",
    screenId: "",
    readOnly: true,
    config,

    // 表示順のバリデーションチェック
    valid: false,
    // 市町村リスト
    municipalities: [],
    // 情報ガイドマスタレコードリスト
    infoGuideMstRecords: [],
    // 情報タイプコード
    infoGuideTypeCode: "00",
    // 市町村コード
    municipCode: "",
    // フリーテキスト
    freeText: "",
    // 編集ダイアログ
    editDialog: false,
    // 編集ダイアログで編集中のレコードのインデックス(新規登録時は-1)
    editedIndex: -1,
    // 編集ダイアログで編集した情報タイプコード
    editedInfoGuideTypeCode: "00",
    // 編集ダイアログで編集した市町村コード
    editedMunicipCode: "",
    // 編集ダイアログで編集したアイテム
    editedItems: createEditedItems()
  }),

  computed: {
    /**
     * バリデーションルール
     */
    validationRules() {
      return getRules();
    },

    /**
     * 編集ダイアログで編集中のアイテムが新規かどうかを示す値
     */
    isNewEditedItems() {
      return this.editedIndex === -1;
    },

    /**
     * 情報タイプリスト
     */
    infoGuideTypes() {
      return getInfoGuideTypes();
    },

    /**
     * 言語リスト
     */
    locales() {
      return getLocales();
    },

    /**
     * 主言語（日本語）コード
     */
    primaryLocaleCode() {
      return this.locales.find(locale => locale.isPrimary).code;
    },

    /**
     * 市町村が必要な情報タイプコード
     */
    municipInfoGuideTypeCodes() {
      return this.infoGuideTypes
        .filter(type => type.needsMunicip)
        .map(type => type.code);
    },

    /**
     * 市町村コードを表示するかどうかを示す値
     */
    showsMunicipalities() {
      return this.municipInfoGuideTypeCodes.includes(this.infoGuideTypeCode);
    },

    /**
     * 説明文が必要な情報タイプコード
     */
    noteInfoGuideTypeCodes() {
      return this.infoGuideTypes
        .filter(type => type.needsNote)
        .map(type => type.code);
    },

    /**
     * 説明文を表示するかどうかを示す値
     */
    showsNote() {
      return this.noteInfoGuideTypeCodes.includes(this.infoGuideTypeCode);
    },

    /**
     * フリーテキストまたはタイトルのラベル
     */
    freeTextLabel() {
      return this.showsNote ? "フリーテキスト" : "タイトル";
    },

    /**
     * フリーテキストまたはタイトルのプレースホルダ
     */
    freeTextPlaceholder() {
      return this.showsNote ? "タイトル、説明文から検索" : "タイトルから検索";
    },

    /**
     * フリーテキストが入力された場合のフィルタ
     */
    freeTextFilter() {
      if (!this.freeText) {
        return () => true;
      }

      return this.showsNote
        ? record =>
            record[this.primaryLocaleCode].title.includes(this.freeText) ||
            record[this.primaryLocaleCode].note?.includes(this.freeText)
        : record =>
            record[this.primaryLocaleCode].title.includes(this.freeText);
    },

    /**
     * 市町村コードが選択された場合のフィルタ
     */
    municipFilter() {
      if (!this.showsMunicipalities) {
        return () => true;
      }

      return record => record.municip_code === this.municipCode;
    },

    /**
     * 一覧表のヘッダー
     */
    tableHeaders() {
      return createTableHeaders(this.showsNote);
    },

    /**
     * 表示順のルール
     */
    dispNoRules() {
      const dispNoArray = this.filteredGuideMstRecords.map(
        record => record.editable_disp_no
      );
      const duplicatedArray = dispNoArray.filter(
        (disp_no, index) => dispNoArray.indexOf(disp_no) !== index
      );

      return [
        this.validationRules.required(),
        this.validationRules.numbersOnly(),
        this.validationRules.duplicated(duplicatedArray)
      ];
    },

    /**
     * 情報ガイドマスタレコードを情報タイプでグループ化したオブジェクト
     */
    groupedInfoGuideMstRecords() {
      const map = Object.fromEntries(
        this.infoGuideTypes.map(guideType => [guideType.code, []])
      );

      for (const record of this.infoGuideMstRecords) {
        if (record.info_guide_type in map) {
          map[record.info_guide_type].push(record);
        } else {
          map[record.info_guide_type] = [record];
        }
      }

      return map;
    },

    /**
     * 現在選択されている情報タイプ、市町村の情報ガイドマスタレコードリスト
     */
    currentGuideMstRecords() {
      return this.groupedInfoGuideMstRecords[this.infoGuideTypeCode].filter(
        this.municipFilter
      );
    },

    /**
     * 情報タイプ、フリーテキスト、市町村でフィルタした情報ガイドマスタレコードリスト
     */
    filteredGuideMstRecords() {
      return this.currentGuideMstRecords.filter(this.freeTextFilter);
    },

    /**
     * 表示順更新ボタンが有効かどうかを示す値
     */
    enableUpdateDispNo() {
      return (
        this.currentGuideMstRecords.length > 0 &&
        this.currentGuideMstRecords.length ===
          this.filteredGuideMstRecords.length
      );
    }
  },

  watch: {
    $route: "initialize",

    /**
     * 情報ガイドコードの変更を監視
     */
    infoGuideTypeCode(newValue) {
      // 編集ダイアログに同期する。
      this.editedInfoGuideTypeCode = newValue;
      this.resetPage();
    },

    /**
     * 市町村コードの変更を監視
     */
    municipCode(newValue) {
      // 編集ダイアログに同期する。
      this.editedMunicipCode = newValue;
      this.resetPage();
    },

    /**
     * フリーテキストの変更を監視
     */
    freeText() {
      this.resetPage();
    },

    /**
     * 情報ガイドマスタレコードリストの変更時に表示順のバリデーションをリセット
     */
    infoGuideMstRecords() {
      this.$refs.form.resetValidation();
    }
  },

  /**
   * ライフサイクルイベント
   */
  created() {
    this.initialize();
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    async initialize() {
      this.overlay = true;
      try {
        // 操作権限
        const screen = this.$screenOperationAuthority(
          ScreenId.SF3101,
          ScreenId.SF3102
        );
        this.screenId = screen.screenId;
        this.readOnly = screen.readOnly;
        this.screenTitle = screen.screenTitle;

        // 初期データ取得
        const [municipalities, infoGuideMstRecords] = await Promise.all([
          fetchMunicipalities(),
          fetchInfoGuideMstRecords(this.screenId)
        ]);

        this.municipalities = municipalities;
        this.municipCode = municipalities[0]?.municip_code ?? "";
        this.infoGuideMstRecords = infoGuideMstRecords;
      } catch (error) {
        this.handleError(error, this.screenId, "aggregation-menu");
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 表示順更新ボタンのクリックイベントを処理する。
     */
    async handleUpdateDispNo() {
      this.clearMessage();

      // 入力フォームのバリデーションチェック
      if (!this.$refs.form.validate()) {
        await this.showErrorDialog(
          this.$refs.form.inputs.map(input => input.errorBucket).flat()[0]
        );

        return;
      }

      const message = "表示順を更新します。よろしいですか？";
      if (!(await this.showConfirmDialog("更新", message))) {
        return;
      }

      this.overlay = true;
      try {
        // 情報ガイドマスタの表示順を更新して再取得
        this.handleSuccess(await this.updateDispNo());
        await this.refreshRecords();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 参照、または、編集ボタンのクリックイベントを処理する。
     */
    handleViewOrEdit(record) {
      // 編集ダイアログの表示
      this.editedIndex = this.infoGuideMstRecords.indexOf(record);
      this.editedItems = toEditedItems(record);
      this.editDialog = true;
    },

    /**
     * 削除ボタンのクリックイベントを処理する。
     */
    async handleDelete(record) {
      this.clearMessage();

      const messages = [
        "削除した場合には元に戻せません。本当によろしいですか？"
      ];
      for (let message of messages) {
        if (!(await this.showConfirmDialog("削除", message))) {
          return;
        }
      }

      this.overlay = true;
      try {
        // 情報ガイドマスタを削除して再取得
        this.handleSuccess(await this.deleteRecord(record));
        await this.refreshRecords();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 編集ダイアログの登録または更新ボタンクリックイベントを処理する。
     */
    async handleSaveEditDialog() {
      this.clearMessage();

      this.overlay = true;
      this.loading = true;
      try {
        // 情報ガイドマスタを登録または更新して再取得
        if (this.isNewEditedItems) {
          this.handleSuccess(await this.createRecord());
        } else {
          this.handleSuccess(await this.updateRecord());
        }
        await this.refreshRecords();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.closeEditDialog();
        this.overlay = false;
        this.loading = false;
      }
    },

    /**
     * 編集ダイアログのキャンセルまたは戻るボタンクリックイベントを処理する。
     */
    handleCloseEditDialog() {
      this.closeEditDialog();
    },

    /**
     * 情報ガイドマスタを取得する。
     */
    async refreshRecords() {
      this.infoGuideMstRecords = await fetchInfoGuideMstRecords(this.screenId);
    },

    /**
     * 情報ガイドマスタを登録する。
     */
    async createRecord() {
      const needsMunicip = this.municipInfoGuideTypeCodes.includes(
        this.editedInfoGuideTypeCode
      );
      const needsNote = this.noteInfoGuideTypeCodes.includes(
        this.editedInfoGuideTypeCode
      );

      return await infoGuideMstRepository.create(
        this.screenId,
        createCreatePayload(
          {
            info_guide_type: this.editedInfoGuideTypeCode,
            municip_code: needsMunicip ? this.editedMunicipCode : undefined
          },
          fromEditedItems(this.editedItems, needsNote)
        )
      );
    },

    /**
     * 情報ガイドマスタを更新する。
     */
    async updateRecord() {
      return await infoGuideMstRepository.update(
        this.screenId,
        createUpdatePayload(
          this.infoGuideMstRecords[this.editedIndex],
          fromEditedItems(this.editedItems)
        )
      );
    },

    /**
     * 情報ガイドマスタの表示順を更新する。
     */
    async updateDispNo() {
      return await infoGuideMstRepository.update(
        this.screenId,
        createUpdateDispNoPayload(this.filteredGuideMstRecords)
      );
    },

    /**
     * 情報ガイドマスタを削除する。
     * @param {Object} record 削除するレコード
     */
    async deleteRecord(record) {
      return await infoGuideMstRepository.delete(
        this.screenId,
        createDeletePayload(record)
      );
    },

    /**
     * 編集ダイアログを閉じる。
     */
    closeEditDialog() {
      this.editDialog = false;

      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedInfoGuideTypeCode = this.infoGuideTypeCode;
        this.editedMunicipCode = this.municipCode;
        this.editedItems = createEditedItems();
      });
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    async showErrorDialog(...messages) {
      await this.$refs.errorDialog.open(messages);
    },

    /**
     * 確認ダイアログを表示する。
     * @param {string} buttonText ボタンに表示するテキスト
     * @param {...string} messages メッセージ
     * @returns {boolean} キャンセルされていないかどうかを示す値。
     */
    async showConfirmDialog(buttonText, ...messages) {
      return await this.$refs.confirmDialog.open(messages, buttonText);
    },

    /**
     * 一覧表のページネーションをリセット
     */
    resetPage() {
      this.$refs.table?.resetPage();
    }
  }
};
</script>

<style scoped>
.align-right-input >>> input {
  text-align: right;
}
</style>
